// apps/client/src/pages/LoanListPage.tsx
// Loan Portfolio page - View and search all loans with commission history

import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useLoanList } from '../hooks/useLoanList';
import { resolveLoanBookSelection } from '../utils/loanBookResolution';
import type { ResolvedLoanBook } from '@valutten/shared-schemas';
import { useTrackEvent } from '../hooks/useTrackEvent';
import { LoanSearchBar } from '../components/loans/LoanSearchBar';
import { LoanListTable } from '../components/loans/LoanListTable';
import { LoanPortfolioStats } from '../components/loans/LoanPortfolioStats';
import { Pagination } from '../components/common/Pagination';
import { FileText } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';

export function LoanListPage() {
  const { loanBooks = [] } = useOutletContext<any>();
  const hasEntityLoanBooks = useMemo(() =>
    (loanBooks as ResolvedLoanBook[]).some((lb: ResolvedLoanBook) => lb.type === 'entity'),
    [loanBooks]
  );
  const [selectedLoanBook, setSelectedLoanBook] = useState('all');
  const resolvedParams = useMemo(
    () => resolveLoanBookSelection(selectedLoanBook, loanBooks),
    [selectedLoanBook, loanBooks]
  );

  const {
    loans,
    total,
    trailStoppedAvailable,
    loading,
    error,
    filters,
    updateFilters,
    clearFilters,
    pagination,
    setPage,
    setPageSize,
    sort,
    updateSort,
  } = useLoanList();
  const { trackEvent } = useTrackEvent();
  const prevSearchRef = useRef(filters.search);

  // Deep link from the Client Care digest: /loans?status=trail_stopped. Applied
  // once, and only after the server says this caller may use the filter (it
  // refuses it otherwise), so the link never produces an error page.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkStatus = searchParams.get('status');
  useEffect(() => {
    if (deepLinkStatus !== 'trail_stopped' || loading || !trailStoppedAvailable) return;
    updateFilters({ status: 'trail_stopped' });
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    setSearchParams(next, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkStatus, loading, trailStoppedAvailable]);

  // Track loan search when search filter changes and results load
  useEffect(() => {
    if (filters.search && filters.search !== prevSearchRef.current && !loading) {
      trackEvent('loan_searched', {
        searchType: 'borrower_name',
        resultCount: total,
      });
    }
    prevSearchRef.current = filters.search;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, loading, total]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={FileText}
        title="Loan Portfolio"
        subtitle="View and search all loans with detailed commission history"
      />

      {/* Loan Book Selector — unified-mode companies only */}
      {hasEntityLoanBooks && (
        <div className="bg-gray-800 rounded-lg p-4 shadow-md border border-gray-700">
          <div className="flex items-center gap-4">
            <label className="text-sm font-medium text-gray-400">Loan Book</label>
            <select
              value={selectedLoanBook}
              onChange={(e) => setSelectedLoanBook(e.target.value)}
              className="bg-gray-700 border-gray-600 text-white rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Loan Books</option>
              {(loanBooks as ResolvedLoanBook[]).map((lb: ResolvedLoanBook) => (
                <option key={lb.id} value={lb.id}>{lb.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <LoanPortfolioStats
        filters={{
          brokerName: filters.brokerName,
          lender: filters.lender,
        }}
        entityCode={resolvedParams.entityCode}
      />

      {/* Search and Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
        <LoanSearchBar
          filters={filters}
          onFiltersChange={updateFilters}
          onClearFilters={clearFilters}
          showTrailStopped={trailStoppedAvailable}
        />
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-400">
          {!loading && (
            <span>
              Showing {loans.length === 0 ? 0 : (pagination.page - 1) * pagination.pageSize + 1} to{' '}
              {Math.min(pagination.page * pagination.pageSize, total)} of{' '}
              <span className="font-semibold text-white">{total.toLocaleString()}</span> loans
            </span>
          )}
        </div>
      </div>

      {/* Error messages */}
      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded-lg p-6">
          <h3 className="text-red-300 font-semibold mb-2">Error Loading Loans</h3>
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Table */}
      <LoanListTable loans={loans} loading={loading} sort={sort} onSortChange={updateSort} />

      {/* Pagination */}
      {!loading && loans.length > 0 && (
        <div className="mt-6">
          <Pagination
            currentPage={pagination.page}
            totalItems={total}
            pageSize={pagination.pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </div>
      )}
    </div>
  );
}
